import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { US_CITIES, US_STATES } from '../../utils/usCitiesStates.js';
import toast from 'react-hot-toast';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const FormCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.xl};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xxl};
  width: 100%;
  max-width: 500px;
  box-shadow: ${props => props.theme.shadows.xl};
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  background: ${props => props.theme.colors.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: ${props => props.theme.typography.h2.fontSize};
  font-weight: ${props => props.theme.typography.h2.fontWeight};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const Label = styled.label`
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.caption.fontSize};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? props.theme.colors.error : props.theme.colors.accent};
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.body.fontSize};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ErrorMessage = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: ${props => props.theme.typography.small.fontSize};
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.gradient};
  border: none;
  color: white;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-size: ${props => props.theme.typography.body.fontSize};
  font-weight: 500;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.caption.fontSize};
`;

const FooterLink = styled(Link)`
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  font-weight: 500;
`;

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    borderColor: state.isFocused ? '#8B5CF6' : '#ccc',
    boxShadow: state.isFocused ? '0 0 0 2px #8B5CF633' : provided.boxShadow,
    minHeight: 40,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: '#fff',
    color: '#222',
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#8B5CF622' : '#fff',
    color: '#222',
    cursor: 'pointer',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#222',
  }),
  input: (provided) => ({
    ...provided,
    color: '#222',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#888',
  }),
};

const Register = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = useForm({
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      username: '',
      user_type: 'fan',
      city: '',
      state: ''
    }
  });

  const password = watch('password');

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Use selected city/state or fallback to form values
      const city = selectedCity ? selectedCity.value : data.city.trim();
      const state = selectedState ? selectedState.value : data.state.trim().toUpperCase();
      
      if (!city || !state) {
        toast.error('Please select your city and state');
        return;
      }
      
      // Create user data for profile
      const userData = {
        username: data.username,
        user_type: data.user_type,
        city: city,
        state: state
      };

      const result = await signUp(data.email, data.password, userData);

      if (result && result.needsEmailConfirmation) {
        toast.success('Account created! Please check your email to verify your account.');
        navigate('/auth/verify-email', { 
          state: { 
            email: data.email,
            message: 'We\'ve sent a verification email to your inbox. Please click the link in the email to activate your account.' 
          } 
        });
      } else {
        toast.success('Account created successfully!');
        navigate('/');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <FormCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Header>
          <Title>Create Account</Title>
        </Header>

        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="Enter your email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              error={errors.email}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Username</Label>
            <Input
              type="text"
              placeholder="Choose a username"
              {...register('username', {
                required: 'Username is required',
                minLength: {
                  value: 3,
                  message: 'Username must be at least 3 characters'
                }
              })}
              error={errors.username}
            />
            {errors.username && <ErrorMessage>{errors.username.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Account Type</Label>
            <select
              {...register('user_type', {
                required: 'Account type is required'
              })}
              style={{
                width: '100%',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: 8,
                padding: 12,
                color: '#222',
                fontSize: 16,
                marginBottom: 4
              }}
              defaultValue="fan"
            >
              <option value="fan">Music Fan</option>
              <option value="band">Band/Artist</option>
              <option value="venue">Venue</option>
            </select>
            {errors.user_type && (
              <ErrorMessage>{errors.user_type.message}</ErrorMessage>
            )}
          </FormGroup>

          <FormGroup style={{ border: '2px solid #8B5CF6', borderRadius: 8, padding: 16, background: '#f8f7ff', marginBottom: 24 }}>
            <Label style={{ fontWeight: 700, fontSize: 18, color: '#8B5CF6' }}>
              Where are you located?
            </Label>
            <CreatableSelect
              options={US_CITIES}
              value={selectedCity}
              onChange={option => {
                setSelectedCity(option);
                setValue('city', option ? option.value : '');
              }}
              placeholder="Select or type your city..."
              isClearable
              styles={customSelectStyles}
            />
            {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
            <Select
              options={US_STATES}
              value={selectedState}
              onChange={option => {
                setSelectedState(option);
                setValue('state', option ? option.value : '');
              }}
              placeholder="Select your state..."
              isClearable
              styles={customSelectStyles}
            />
            {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder="Create a password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters'
                }
              })}
              error={errors.password}
            />
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Confirm Password</Label>
            <Input
              type="password"
              placeholder="Confirm your password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: value => value === password || 'Passwords do not match'
              })}
              error={errors.confirmPassword}
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
          </FormGroup>

          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </SubmitButton>
        </Form>

        <Footer>
          Already have an account?{' '}
          <FooterLink to="/login">Sign in</FooterLink>
        </Footer>
      </FormCard>
    </Container>
  );
};

export default Register; 